import { Injectable } from '@angular/core';
import { AuthConfig, OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import {
  aldconf,
  environment,
  fordconf,
  fordFmconf,
  hondaconf,
  jaguarconf,
  landroverconf,
  lynkcoconf,
  omodajaecooconf,
  mgconf,
  smartconf,
  suzukiconf,
  fordfleetmanagementconf
} from 'src/environments/environment';
import jwt_decode from 'jwt-decode';
import { SubSink } from 'subsink';

import { ApiSearchUtilService } from '../shared/service/api-search-util/api-search-util.service';
import { ProfileManagementService } from '../shared/service/store-management/profile-management/profile-management.service';
import { AUTH_ENUM } from './model/auth.enum';

// Sessione parcheggiata durante il giro sul logout dell'IdP. Il consenso WSO2 puo'
// essere rifiutato ("No"): in quel caso rientriamo nell'app e va rimessa la sessione
// esatta di prima, altrimenti AppComponent.isProfileChosen() la vede vuota e riapre
// la Gestione profilo (o la scelta cliente, per il Customer Service).
const PRE_LOGOUT_SESSION_KEY = 'pre_logout_session';

@Injectable({
  providedIn: "root",
})
export class RfAuthenticationService {
  authConfig: AuthConfig;
  private isLoggedSubject = new BehaviorSubject(false);
  private subs = new SubSink();
  isLogged$ = false;

  tokenSubscription: Subscription;
  basePath = window.location.href;

  constructor(
    private oauthService: OAuthService,
    private profileManagementService: ProfileManagementService,
    private apiSearchUtilService: ApiSearchUtilService
  ) { }

  getConfiguration(): any {
    switch (this.basePath) {
      case this.apiSearchUtilService.findTerm(this.basePath, "fordfleetmanagement"):
        return of(fordfleetmanagementconf);
      case this.apiSearchUtilService.findTerm(this.basePath, "ford"):
        return of(fordconf);
      case this.apiSearchUtilService.findTerm(this.basePath, "ald"):
        return of(aldconf);
      case this.apiSearchUtilService.findTerm(this.basePath, "suzuki"):
        return of(suzukiconf);
      case this.apiSearchUtilService.findTerm(this.basePath, "honda"):
        return of(hondaconf);
      case this.apiSearchUtilService.findTerm(this.basePath, "landrover"):
        return of(landroverconf);
      case this.apiSearchUtilService.findTerm(this.basePath, "jaguar"):
        return of(jaguarconf);
      case this.apiSearchUtilService.findTerm(this.basePath, "lynkco"):
        return of(lynkcoconf);
      case this.apiSearchUtilService.findTerm(this.basePath, "smart"):
        return of(smartconf);
      case this.apiSearchUtilService.findTerm(this.basePath, "omodajaecoo"):
        return of(omodajaecooconf);
      case this.apiSearchUtilService.findTerm(this.basePath, "mg"):
        return of(mgconf);
      default:
        return of(aldconf); //rimettere aldconf post test new captive
    }
  }

  public isLogIn(): boolean {
    return !!this.oauthService.getIdToken();
  }

  configureAndRefresh(associationCode?: string): void {
    this.loginProcess(associationCode);
    this.checkIfTokenExpire();
  }

  loadConfiguration(associationCode?: string) {
    this.subs.add(
      this.getConfiguration().subscribe((configurationLoaded: AuthConfig) => {
        this.profileManagementService.setAssCode(associationCode);
        configurationLoaded.redirectUri = `${this.checkLocation()}${this.checkIfExistAssociationCode(
          associationCode
        )}`;
        configurationLoaded.postLogoutRedirectUri = this.checkLocation();
        configurationLoaded.disableAtHashCheck;
        if (environment.mobile) {
          configurationLoaded.openUri = (uri: string) => {
            window.open(uri, "oauth:myald");
          };
        }
        configurationLoaded.skipIssuerCheck = true;
        configurationLoaded.strictDiscoveryDocumentValidation = false;
        this.configureOauthService(configurationLoaded);
      })
    );
  }

  loginProcess(associationCode?: string): void {
    if (this.oauthService.getIdToken() === null) {
      this.loadConfiguration(associationCode);
    }
  }

  checkIfExistAssociationCode(associationCode: string): string {
    return !!associationCode ? `/?associationCode=${associationCode}` : ``;
  }

  checkLocation(): string {
    const baseUrl = window.location.origin;
    if (environment.mobile) {
      return "myald://oauth_callback";
    } else if (baseUrl === "http://localhost:4200") {
      return `${baseUrl}/`;
    } else {
      return `${baseUrl}/`;
    }
  }

  configureOauthService(authConfig): void {
    return this.getAuthorizeClientCode(authConfig);
  }

  checkIfTokenExpire(): Subscription {
    return this.oauthService.events
      .pipe(filter((e) => e.type === AUTH_ENUM.TOKEN_EXPIRE))
      .subscribe((e) => {
        this.loginProcess();
      });
  }

  async refreshTokenOnLoadIfNeeded(): Promise<void> {
    this.subs.add(
      (this.tokenSubscription = this.oauthService.events.subscribe((e) => {
        if (e instanceof OAuthErrorEvent) {
          console.error(e);
          this.logout();
        } else {
          console.warn(e);
          this.logout();
        }
      }))
    );
  }


  getAuthorizeClientCode(authCodeFlowConfig): void {
    authCodeFlowConfig.skipIssuerCheck = true;
    authCodeFlowConfig.strictDiscoveryDocumentValidation = false;
    authCodeFlowConfig.showDebugInformation = true;

    this.oauthService.configure(authCodeFlowConfig);

    console.log('AUTH CONFIG:', authCodeFlowConfig);
    console.log('CURRENT URL:', window.location.href);

    this.oauthService.loadDiscoveryDocumentAndTryLogin()
      .then((logged) => {
        console.log('logged:', logged);
        console.log('hasValidIdToken:', this.oauthService.hasValidIdToken());
        console.log('hasValidAccessToken:', this.oauthService.hasValidAccessToken());
        console.log('id_token:', this.oauthService.getIdToken());
        console.log('access_token:', this.oauthService.getAccessToken());

        if (
          this.oauthService.hasValidIdToken() ||
          this.oauthService.hasValidAccessToken()
        ) {
          console.log('Login riuscito');

          // Rientro da un logout rifiutato: rimetto la sessione di prima PRIMA di
          // setIsLogged(true), che notifica AppComponent e fa partire loadUserInfo().
          if (sessionStorage.getItem('post_logout_recovery') === '1') {
            if (this.restoreSessionAfterAbortedLogout()) {
              // sessionStorage e' tornato a posto, ma header e menu ascoltano gli store
              // in memoria: senza rinotificarli restano su 'User' con il menu ridotto.
              this.profileManagementService.rehydrateProfileFromStore();
            }
          } else {
            // Logout andato davvero a buon fine: lo snapshot non serve piu'.
            sessionStorage.removeItem(PRE_LOGOUT_SESSION_KEY);
          }
          sessionStorage.removeItem('post_logout_recovery');

          this.setIsLogged(true);

          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );

          return;
        }

        if (this.isOAuthCallback()) {
          console.error(
            'Sono tornato dal provider con code/state, ma non ho token. NON rilancio initCodeFlow per evitare loop.'
          );

          return;
        }

        console.log('Nessun token valido, avvio initCodeFlow');
        this.oauthService.initCodeFlow();
      })
      .catch((err) => {
        console.error('Errore durante OAuth login:', err);
           // Ritorno dal provider con un error response (es. "No" al consenso di
        // logout WSO2 → error_description="End User denied the logout request").
        // La sessione IdP è ancora viva: se non abbiamo un token valido rilanciamo
        // il code flow per ri-loggare l'utente in modo silenzioso. Guard one-shot
        // in sessionStorage per evitare loop se la sessione IdP non fosse viva.
        const noToken =
          !this.oauthService.hasValidIdToken() &&
          !this.oauthService.hasValidAccessToken();
        const alreadyTried =
          sessionStorage.getItem('post_logout_recovery') === '1';
        if (noToken && !alreadyTried) {
          sessionStorage.setItem('post_logout_recovery', '1');
          window.history.replaceState({}, document.title, window.location.pathname);
          this.oauthService.initCodeFlow();
        }
      });
  }


  private isOAuthCallback(): boolean {
    const params = new URLSearchParams(window.location.search);
    return params.has('code') && params.has('state');
  }


  hasValidIdToken(): boolean {
    return this.oauthService.hasValidIdToken();
  }

  getTokenId(): string {
    return this.oauthService.getIdToken();
  }

  public logout(idTokenHint?: string): void {
    // Cattura l'id_token prima di svuotare lo storage: il logout RP-initiated
    // deve inviare id_token_hint, altrimenti WSO2 mostra la pagina di consenso
    // e non valida/onora post_logout_redirect_uri.
    const idToken = idTokenHint ?? this.oauthService.getIdToken();
    this.snapshotSessionBeforeLogout(idToken);
    this.oauthService.logOut(idToken ? { id_token_hint: idToken } : {});
  }

  // Fotografa sessionStorage e lo rimette sotto un'unica chiave, che sopravvive al
  // redirect verso l'IdP perche' sessionStorage e' per-tab e resta sulla stessa origin.
  // Lo snapshot viene legato al "sub" dell'utente che ha chiesto il logout: se il
  // re-login non fosse silenzioso e rientrasse qualcun altro, non gli va consegnata
  // la sessione del precedente.
  private snapshotSessionBeforeLogout(idToken?: string): void {
    const entries = {};
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key !== PRE_LOGOUT_SESSION_KEY) {
        entries[key] = sessionStorage.getItem(key);
      }
    }
    const owner = this.getTokenSubject(idToken);
    sessionStorage.clear();
    sessionStorage.setItem(PRE_LOGOUT_SESSION_KEY, JSON.stringify({ owner, entries }));
  }

  private restoreSessionAfterAbortedLogout(): boolean {
    const raw = sessionStorage.getItem(PRE_LOGOUT_SESSION_KEY);
    sessionStorage.removeItem(PRE_LOGOUT_SESSION_KEY);
    if (!raw) {
      return false;
    }
    try {
      const { owner, entries } = JSON.parse(raw);
      if (!owner || owner !== this.getTokenSubject(this.oauthService.getIdToken())) {
        console.warn('Sessione precedente di un altro utente: non ripristinata.');
        return false;
      }
      Object.keys(entries).forEach((key) => sessionStorage.setItem(key, entries[key]));
      return true;
    } catch (err) {
      // Snapshot illeggibile: meglio ripartire dalla scelta profilo che bloccare il login.
      console.error('Snapshot di sessione non ripristinabile:', err);
      return false;
    }
  }

  private getTokenSubject(token: string): string {
    try {
      return !!token ? jwt_decode<any>(token)?.sub : null;
    } catch (err) {
      return null;
    }
  }

  setIsLogged(isLogged: boolean): void {
    this.isLoggedSubject.next(isLogged);
  }

  isLogged(): Observable<any> {
    return this.isLoggedSubject.asObservable();
  }
}
